'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Rocket,
  Building2,
  Settings2,
  TrendingUp,
  Calendar,
  Mail,
} from 'lucide-react';
import { trackLeadFormSubmission, trackScheduleMeetingClick, trackFormStart } from '@/lib/analytics';

const DRATA_CONSENT_TEXT =
  'We’re a Drata partner. Tick this box if you agree we may share your details with Drata to arrange a demo and partner pricing.';

/* ------------------------------------------------------------------ */
/* Assessment model                                                    */
/* ------------------------------------------------------------------ */

interface Choice {
  label: string;
  /** 0 = least mature, 3 = most mature */
  score: number;
}

interface Question {
  id: string;
  text: string;
  /** Short label used when summarizing answers in the lead notification. */
  summary: string;
  choices: Choice[];
}

const QUESTIONS: Question[] = [
  {
    id: 'owner',
    summary: 'GRC owner',
    text: 'Who owns compliance and security governance (GRC) at your company today?',
    choices: [
      { label: 'No one is formally responsible yet', score: 0 },
      { label: 'It is a part-time job for a founder or IT lead', score: 1 },
      { label: 'We have a dedicated head of GRC with a small team', score: 2 },
      { label: 'A CISO/GRC leader runs a mature program', score: 3 },
    ],
  },
  {
    id: 'frameworks',
    summary: 'Frameworks in place',
    text: 'How many compliance frameworks are you actively maintaining right now?',
    choices: [
      { label: 'None yet — we are just getting started', score: 0 },
      { label: 'Working toward our first (e.g. SOC 2 or HIPAA)', score: 1 },
      { label: 'One in place, expanding to a second', score: 2 },
      { label: 'Multiple frameworks maintained continuously', score: 3 },
    ],
  },
  {
    id: 'monitoring',
    summary: 'Evidence & monitoring',
    text: 'How do you collect evidence and monitor controls?',
    choices: [
      { label: 'Spreadsheets and screenshots, gathered before each audit', score: 0 },
      { label: 'Mostly manual, with a few scheduled reminders', score: 1 },
      { label: 'Some automated evidence collection in place', score: 2 },
      { label: 'Continuous monitoring with automated evidence', score: 3 },
    ],
  },
  {
    id: 'vendorRisk',
    summary: 'Vendor/3rd-party risk',
    text: 'How do you manage vendor and third-party risk?',
    choices: [
      { label: 'We do not have a process for this', score: 0 },
      { label: 'Ad hoc — we review a vendor when something prompts it', score: 1 },
      { label: 'We have a defined intake and review process', score: 2 },
      { label: 'Continuous third-party risk monitoring', score: 3 },
    ],
  },
  {
    id: 'deals',
    summary: 'Deals lost to questionnaires',
    text: 'Are security questionnaires or missing certifications slowing down or losing deals?',
    choices: [
      { label: 'Yes — it is actively costing us deals', score: 0 },
      { label: 'Occasionally — it creates delays', score: 1 },
      { label: 'Rarely — we can usually satisfy buyers', score: 2 },
      { label: 'No — our Trust Center handles most of it', score: 3 },
    ],
  },
  {
    id: 'audit',
    summary: 'Audit status',
    text: 'What is your audit situation?',
    choices: [
      { label: 'We have never been through an audit', score: 0 },
      { label: 'A first audit is coming up and we are not ready', score: 1 },
      { label: 'We pass audits but they consume a lot of time', score: 2 },
      { label: 'Audits are smooth and largely automated', score: 3 },
    ],
  },
  {
    id: 'driver',
    summary: 'Primary driver',
    text: 'What is driving compliance for you right now?',
    choices: [
      { label: 'A first enterprise customer or investor is requiring it', score: 0 },
      { label: 'We need it to expand revenue and move upmarket', score: 1 },
      { label: 'Scaling the program across more frameworks and teams', score: 2 },
      { label: 'M&A, or turning GRC into a revenue driver', score: 3 },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Maturity stages (Drata 4-stage model)                               */
/* ------------------------------------------------------------------ */

interface Stage {
  key: 'start' | 'establish' | 'manage' | 'optimize';
  name: string;
  icon: typeof Rocket;
  tagline: string;
  where: string;
  next: string;
}

const STAGES: Stage[] = [
  {
    key: 'start',
    name: 'Start',
    icon: Rocket,
    tagline: 'First-time compliance, building the foundation',
    where:
      'You are early in your compliance journey — likely no dedicated GRC owner yet, and an enterprise customer or investor is pushing you toward your first framework.',
    next:
      'Recommended next step: stand up your first framework (usually SOC 2 or HIPAA) and get audit-ready. We map your controls, close gaps, and connect you with Drata to automate evidence so you are not living in spreadsheets.',
  },
  {
    key: 'establish',
    name: 'Establish',
    icon: Building2,
    tagline: 'One framework live, expanding to more',
    where:
      'You have a functional head of GRC and at least one framework, but the team is small and most work is still manual. Compliance is now tied to expanding revenue.',
    next:
      'Recommended next step: add your next framework without doubling the workload. We help you reuse controls across SOC 2, ISO 27001, and HIPAA and turn on continuous monitoring — teams here typically see roughly 3x faster audits.',
  },
  {
    key: 'manage',
    name: 'Manage',
    icon: Settings2,
    tagline: 'Scaling across frameworks with proactive monitoring',
    where:
      'You have a dedicated GRC leader, multiple frameworks, and you are scaling. The priorities now are proactive monitoring and robust third-party (vendor) risk.',
    next:
      'Recommended next step: tighten third-party risk and accelerate security reviews. A Trust Center can deflect up to 98% of inbound questionnaires (cutting a 2–3 hour task to 15–20 minutes) so your team stops answering the same questions by hand.',
  },
  {
    key: 'optimize',
    name: 'Optimize',
    icon: TrendingUp,
    tagline: 'Mature GRC, compliance as a revenue driver',
    where:
      'You run a mature GRC program with continuous monitoring across many frameworks. Compliance is a competitive advantage — and a factor in M&A.',
    next:
      'Recommended next step: make GRC a true revenue driver. We help you consolidate tooling, automate questionnaire responses across portals, and extend governance into newer frameworks (e.g. ISO 42001 for AI) — with an average reported 3.2x ROI.',
  },
];

function stageForScore(total: number, max: number): Stage {
  const pct = max > 0 ? total / max : 0;
  if (pct < 0.3) return STAGES[0];
  if (pct < 0.55) return STAGES[1];
  if (pct < 0.8) return STAGES[2];
  return STAGES[3];
}

/* ------------------------------------------------------------------ */
/* Framework options for the capture step                              */
/* ------------------------------------------------------------------ */

const FRAMEWORKS = [
  'SOC 2',
  'ISO 27001',
  'HIPAA',
  'PCI DSS',
  'GDPR',
  'NIST CSF',
  'CMMC',
  'HITRUST',
  'ISO 42001 (AI)',
  'Not sure yet',
];

type Phase = 'quiz' | 'result' | 'success';
type SubmitStatus = 'idle' | 'submitting' | 'error';

const MAX_SCORE = QUESTIONS.length * 3;

export default function AssessmentClient() {
  const [phase, setPhase] = useState<Phase>('quiz');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [formStarted, setFormStarted] = useState(false);

  // Capture fields
  const [email, setEmail] = useState('');
  const [employees, setEmployees] = useState('');
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [honeypot, setHoneypot] = useState('');
  // Optional: only leads who tick this may be registered with Drata (deal registration).
  const [drataConsent, setDrataConsent] = useState(false);

  const [errors, setErrors] = useState<{ email?: string; employees?: string; frameworks?: string }>({});
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');

  const [loadTime] = useState(() => Date.now());

  const totalScore = useMemo(
    () => QUESTIONS.reduce((sum, q) => sum + (answers[q.id] ?? 0), 0),
    [answers]
  );
  const answeredCount = Object.keys(answers).length;
  const stage = useMemo(() => stageForScore(totalScore, MAX_SCORE), [totalScore]);

  const startTracking = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Compliance Readiness Assessment', 'compliance_readiness_assessment');
    }
  };

  const handleAnswer = (questionId: string, score: number) => {
    startTracking();
    setAnswers((prev) => ({ ...prev, [questionId]: score }));
    // Auto-advance after a brief beat for visual feedback.
    if (step < QUESTIONS.length - 1) {
      setStep((s) => s + 1);
    } else {
      setPhase('result');
    }
  };

  const toggleFramework = (fw: string) => {
    setSelectedFrameworks((prev) =>
      prev.includes(fw) ? prev.filter((f) => f !== fw) : [...prev, fw]
    );
    if (errors.frameworks) setErrors((e) => ({ ...e, frameworks: undefined }));
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!email.trim()) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email address';
    if (!employees.trim()) next.employees = 'Please select your team size';
    if (selectedFrameworks.length === 0) next.frameworks = 'Pick at least one framework (or “Not sure yet”)';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildMessage = (): string => {
    const answerLines = QUESTIONS.map((q) => {
      const score = answers[q.id];
      const choice = q.choices.find((c) => c.score === score);
      return `- ${q.summary}: ${choice ? choice.label : 'No answer'}`;
    }).join('\n');

    return [
      `[Topic: Compliance Readiness Assessment]`,
      ``,
      `Maturity stage: ${stage.name} (score ${totalScore}/${MAX_SCORE})`,
      `Number of employees: ${employees}`,
      `Frameworks needed: ${selectedFrameworks.join(', ')}`,
      ``,
      `Assessment answers:`,
      answerLines,
      ``,
      drataConsent
        ? `Consent to share with Drata: YES (checked ${new Date().toISOString()}) - "${DRATA_CONSENT_TEXT}"`
        : `Consent to share with Drata: NO - do not register this lead with Drata`,
    ].join('\n');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot — silently pretend success for bots.
    if (honeypot) {
      setPhase('success');
      return;
    }
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');
    setEmailSuggestion('');

    const nameParts = name.trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || 'Compliance';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : 'Lead';

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.trim(),
          company: company.trim(),
          message: buildMessage(),
          formName: 'Compliance Readiness Assessment',
          src: 'drata-referral',
          cat: 'compliance',
          id: `stage-${stage.key}`,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          _t: loadTime,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (typeof data.suggestion === 'string' && data.suggestion) {
          setEmailSuggestion(data.suggestion);
        }
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }

      trackLeadFormSubmission(
        'Compliance Readiness Assessment',
        'compliance_readiness_assessment',
        undefined,
        email.trim()
      );

      setPhase('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const applySuggestion = () => {
    setEmail(emailSuggestion);
    setEmailSuggestion('');
    setErrors((e) => ({ ...e, email: undefined }));
    setStatus('idle');
  };

  /* ---------------------------------------------------------------- */
  /* SUCCESS                                                           */
  /* ---------------------------------------------------------------- */
  if (phase === 'success') {
    const StageIcon = stage.icon;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 md:p-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full mb-6">
          <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          Your roadmap is on the way.
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
          We&apos;ve received your assessment. A compliance expert will email you a personalized roadmap
          for the <span className="font-semibold text-gray-800 dark:text-gray-200">{stage.name}</span> stage
          &mdash; usually the same business day.
        </p>

        <div className="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl mb-8">
          <StageIcon className="w-6 h-6 text-[#2813e8]" />
          <span className="text-[#2813e8] font-semibold">Your stage: {stage.name}</span>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 dark:border-blue-900 rounded-xl p-6 mb-6 max-w-xl mx-auto">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Want to skip the wait? Book your roadmap review right now.
          </p>
          <a
            href="https://hs.inventivehq.com/meetings/sean1324/contactpage"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackScheduleMeetingClick('compliance_readiness_success')}
            className="inline-flex items-center justify-center gap-2 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            <Calendar className="w-5 h-5" />
            Schedule a Call Now
          </a>
        </div>

        <Link
          href="/services/compliance"
          className="text-[#2813e8] hover:text-[#2cc3d1] font-medium transition-colors"
        >
          Explore our compliance services &rarr;
        </Link>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* RESULT + CAPTURE                                                  */
  /* ---------------------------------------------------------------- */
  if (phase === 'result') {
    const StageIcon = stage.icon;
    return (
      <div className="space-y-8">
        {/* Result card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="bg-gradient-to-br from-[#2813e8] to-[#2cc3d1] text-white p-8 md:p-10">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-3">
              <CheckCircle2 className="w-4 h-4" />
              Assessment complete
            </div>
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-16 h-16 bg-white/15 rounded-2xl flex items-center justify-center">
                <StageIcon className="w-9 h-9" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider text-white/80 mb-1">Your maturity stage</p>
                <h3 className="text-3xl md:text-4xl font-bold mb-2">{stage.name}</h3>
                <p className="text-white/90">{stage.tagline}</p>
              </div>
            </div>

            {/* Stage progress */}
            <div className="mt-8 grid grid-cols-4 gap-2">
              {STAGES.map((s) => (
                <div key={s.key} className="text-center">
                  <div
                    className={`h-1.5 rounded-full mb-2 ${
                      s.key === stage.key ? 'bg-white' : 'bg-white/30'
                    }`}
                  />
                  <span
                    className={`text-xs ${
                      s.key === stage.key ? 'text-white font-semibold' : 'text-white/60'
                    }`}
                  >
                    {s.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-8 md:p-10 space-y-6">
            <div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Where you are</h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{stage.where}</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 dark:border-blue-900 rounded-xl p-6">
              <h4 className="text-lg font-bold text-[#2813e8] mb-2">Your recommended next step</h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{stage.next}</p>
            </div>
          </div>
        </div>

        {/* Lead capture */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 md:p-10">
          <div className="mb-6">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Get your personalized compliance roadmap
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Tell us where to send it and a CISSP-led compliance expert will map your fastest path to
              audit-ready &mdash; and connect you with the right automation through our Drata partnership.
              No obligation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" name="Compliance Readiness Assessment">
            {/* Honeypot */}
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label htmlFor="cr-website">Website</label>
              <input
                type="text"
                id="cr-website"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Email (required) */}
            <div>
              <label htmlFor="cr-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Work email<span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                type="email"
                id="cr-email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((er) => ({ ...er, email: undefined }));
                  if (emailSuggestion) setEmailSuggestion('');
                }}
                onFocus={startTracking}
                aria-invalid={!!errors.email}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.email
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
                } bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 transition-colors`}
                placeholder="you@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Number of employees (required — Drata quote input #1) */}
            <div>
              <label htmlFor="cr-employees" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of employees<span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <select
                id="cr-employees"
                name="employees"
                value={employees}
                onChange={(e) => {
                  setEmployees(e.target.value);
                  if (errors.employees) setErrors((er) => ({ ...er, employees: undefined }));
                }}
                onFocus={startTracking}
                aria-invalid={!!errors.employees}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.employees
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-[#2813e8] focus:border-[#2813e8]'
                } bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 transition-colors ${
                  employees ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <option value="">Select team size...</option>
                <option value="1-10">1–10</option>
                <option value="11-50">11–50</option>
                <option value="51-200">51–200</option>
                <option value="201-500">201–500</option>
                <option value="501-1000">501–1,000</option>
                <option value="1000+">1,000+</option>
              </select>
              {errors.employees && (
                <p className="mt-1 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.employees}
                </p>
              )}
            </div>

            {/* Frameworks (required — Drata quote input #2) */}
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Which frameworks do you need?<span className="text-red-600 dark:text-red-400">*</span>
                <span className="font-normal text-gray-500 dark:text-gray-400"> (select all that apply)</span>
              </span>
              <div className="flex flex-wrap gap-2">
                {FRAMEWORKS.map((fw) => {
                  const active = selectedFrameworks.includes(fw);
                  return (
                    <button
                      key={fw}
                      type="button"
                      onClick={() => {
                        startTracking();
                        toggleFramework(fw);
                      }}
                      aria-pressed={active}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                        active
                          ? 'bg-[#2813e8] text-white border-[#2813e8]'
                          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[#2cc3d1]'
                      }`}
                    >
                      {fw}
                    </button>
                  );
                })}
              </div>
              {errors.frameworks && (
                <p className="mt-2 text-sm text-red-700 dark:text-red-300 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.frameworks}
                </p>
              )}
            </div>

            {/* Optional: name + company */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="cr-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="cr-name"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onFocus={startTracking}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-[#2813e8] focus:border-[#2813e8] focus:outline-none focus:ring-2 transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="cr-company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="cr-company"
                  name="company"
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  onFocus={startTracking}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-[#2813e8] focus:border-[#2813e8] focus:outline-none focus:ring-2 transition-colors"
                  placeholder="Acme Inc."
                />
              </div>
            </div>

            {/* Error */}
            {status === 'error' && (
              <div role="alert" className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 dark:text-red-300 font-medium">Could not submit</p>
                  <p className="text-red-700 dark:text-red-300 text-sm">{errorMessage}</p>
                  {emailSuggestion && (
                    <p className="mt-2 text-sm text-red-700 dark:text-red-300">
                      Did you mean{' '}
                      <button
                        type="button"
                        onClick={applySuggestion}
                        className="font-semibold text-[#2813e8] underline hover:text-[#2cc3d1] transition-colors"
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </p>
                  )}
                </div>
              </div>
            )}

            <label className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={drataConsent}
                onChange={(e) => setDrataConsent(e.target.checked)}
                className="h-4 w-4 mt-0.5"
              />
              <span>
                {DRATA_CONSENT_TEXT} (Optional.) See our{' '}
                <Link href="/privacy-policy" className="underline">
                  privacy policy
                </Link>
                .
              </span>
            </label>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full px-8 py-4 bg-[#2813e8] hover:bg-[#1f0fb8] disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Get My Compliance Roadmap
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              We respect your privacy. No spam, ever. View our{' '}
              <Link href="/privacy-policy" className="underline hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                Privacy Policy
              </Link>
              .
            </p>
          </form>

          <button
            onClick={() => {
              setPhase('quiz');
              setStep(QUESTIONS.length - 1);
            }}
            className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-[#2813e8] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to questions
          </button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* QUIZ                                                              */
  /* ---------------------------------------------------------------- */
  const question = QUESTIONS[step];
  const currentAnswer = answers[question.id];
  const progressPct = Math.round((answeredCount / QUESTIONS.length) * 100);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 md:p-10">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
          <span className="font-semibold text-[#2813e8]">
            Question {step + 1} of {QUESTIONS.length}
          </span>
          <span>{progressPct}% complete</span>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {question.text}
      </h3>

      <div className="space-y-3">
        {question.choices.map((choice) => {
          const selected = currentAnswer === choice.score;
          return (
            <button
              key={choice.label}
              type="button"
              onClick={() => handleAnswer(question.id, choice.score)}
              className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center gap-4 group ${
                selected
                  ? 'border-[#2813e8] bg-blue-50 dark:bg-blue-950/30 ring-2 ring-[#2813e8]/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#2cc3d1] hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <span
                className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  selected
                    ? 'border-[#2813e8] bg-[#2813e8]'
                    : 'border-gray-300 dark:border-gray-600 group-hover:border-[#2cc3d1]'
                }`}
              >
                {selected && <CheckCircle2 className="w-4 h-4 text-white" />}
              </span>
              <span className="text-gray-800 dark:text-gray-200 font-medium">{choice.label}</span>
            </button>
          );
        })}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between mt-8">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-[#2813e8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {currentAnswer !== undefined && (
          <button
            type="button"
            onClick={() => {
              if (step < QUESTIONS.length - 1) setStep((s) => s + 1);
              else setPhase('result');
            }}
            className="inline-flex items-center gap-2 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            {step < QUESTIONS.length - 1 ? 'Next' : 'See my result'}
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
